import React from 'react';
import { Accordion, Switch, Typography } from '@scale/scaleui';
import { MultiTurnInteractionConfiguration } from '@scale/shared/types/chat';
import ConfigSectionHeader from '../components/ConfigSectionHeader';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import { ExperimentalFeature } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatConfigurationEditor/components/ConfigLifeCycleInfo';

interface ChatHistoryProps {
  value: MultiTurnInteractionConfiguration;
  onSubmit: (config: MultiTurnInteractionConfiguration) => void;
  useAccordion?: boolean;
}

const ChatHistory = ({ value, onSubmit, useAccordion = true }: ChatHistoryProps) => {
  const WrappingComponent = useAccordion ? Accordion : ConfigSectionHeader;
  return (
    <div className="flex flex-col gap-2">
      <Typography.H3>Chat History Options</Typography.H3>
      <Typography.Paragraph2 color="text-neutral-500">
        Configure chat history options that affect the entire conversation. This includes actions
        such as restricting reviewer prompt editing, adding or deleting turns, and forcing chat
        history resets.
      </Typography.Paragraph2>

      <WrappingComponent header={'Chat History Options'}>
        <div className="space-y-4">
          <div className="space-y-2">
            <Switch
              altDescription="Prevent Reviewers From Modifying Past Chat History"
              label="Prevent Reviewers From Modifying Past Chat History"
              checked={value.restrictRollbacksInReview}
              onCheckedChange={checked =>
                onSubmit({ ...value, restrictRollbacksInReview: checked })
              }
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Enabling this will prevent reviewers from editing any chat history that has already
              occurred. It removes the button to “restart” the chat at a previous prompt step in the
              task.
            </Typography.Paragraph2>
          </div>
          <div className="space-y-2">
            <Switch
              altDescription="Force History Reset (when prompts are edited)"
              label="Force History Reset on Prompt Edits"
              checked={value.forceHistoryReset}
              onCheckedChange={checked => onSubmit({ ...value, forceHistoryReset: checked })}
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Enabling this will force contributors to always reset the chat history when editing
              the prompt. Enable this if to ensure that the most up-to-date prompts are shown in L4
              after an SBQ. This takes precedent over the Prevent Reviewers From Modifying Past Chat
              History.
            </Typography.Paragraph2>
          </div>
          <div className="space-y-2">
            <Switch
              altDescription="[Uncommon] Force History Reset (when responses are edited)"
              label="[Uncommon] Force History Reset on Response Edits"
              checked={value.forceHistoryResetForResponseChanges}
              onCheckedChange={checked =>
                onSubmit({ ...value, forceHistoryResetForResponseChanges: checked })
              }
            />
            <Typography.Paragraph2 color="text-neutral-500">
              ⚠️ Be careful! ⚠️ When this option is enabled, if contributors edit a response, all
              subsequent steps will be cleared (even if they are not visible). This is a rare
              config, and should only be used in cases where editing a response after subsequent
              steps (e.g. ratings) are populated could result in major inconsistencies.
            </Typography.Paragraph2>
          </div>
          <div className="space-y-2">
            <Switch
              altDescription="Enable Last Turn Addition"
              label="Enable Last Turn Addition"
              checked={value.enableLastTurnEditing}
              onCheckedChange={checked => onSubmit({ ...value, enableLastTurnEditing: checked })}
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Enabling this will allow users to add a new last turn to existing chat sessions that
              have ended. Users will be able to "continue" the conversation.
            </Typography.Paragraph2>
          </div>
          <div className="space-y-2">
            <Switch
              altDescription="Enable Mass Turn Deletion"
              label="Enable Mass Turn Deletion"
              checked={value.enableMassTurnDeletion}
              onCheckedChange={checked => onSubmit({ ...value, enableMassTurnDeletion: checked })}
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Enabling this will allow users to delete the entire chat history after a particular
              turn. It can also be used to delete the last turn in a conversation.
            </Typography.Paragraph2>
          </div>
          <div className="space-y-2">
            <Switch
              altDescription="[Advanced] Enable Conversation Editing"
              label="[Advanced] Enable Conversation Editing"
              checked={value.enableConversationEditing}
              onCheckedChange={checked =>
                onSubmit({ ...value, enableConversationEditing: checked })
              }
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Enabling this will allow users to perform various edits to the entire chat session,
              including the middle of the conversation. This includes reordering, deleting, and
              duplicating turns. Do not enable this if your project depends on the context of the
              previous turns (i.e. if you are using the output from previous turns to generate the
              next model response).
            </Typography.Paragraph2>
          </div>
          <div className="space-y-2">
            <Switch
              altDescription="[Advanced] Reset Chat History on Each Turn"
              label="[Advanced] Reset Chat History on Each Turn"
              checked={value.resetChatHistoryOnEachTurn}
              onCheckedChange={checked =>
                onSubmit({ ...value, resetChatHistoryOnEachTurn: checked })
              }
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Enabling this will reset the chat history at the start of every turn. This will mean
              that each turn is essentially an independent conversation with no memory of the
              earlier conversation.
            </Typography.Paragraph2>
            {value.resetChatHistoryOnEachTurn && (
              <div className="space-y-2">
                <Switch
                  altDescription="[Multimodal] Pass Down Original Attachments"
                  label="[Multimodal] Pass Down Original Attachments"
                  checked={value.passDownOriginalAttachments}
                  onCheckedChange={checked =>
                    onSubmit({ ...value, passDownOriginalAttachments: checked })
                  }
                />
                <Typography.Paragraph2 color="text-neutral-500">
                  Enable this option if you are resetting the chat history on each turn, but want
                  all turns to have visibility into the original prompt's attachments.
                </Typography.Paragraph2>
              </div>
            )}
          </div>
          <ExperimentalFeature
            name="Extract model response regex"
            details="If present, the model response shown in the chat turn overview (when closed) will be extracted using the provided regex."
            showInfoCondition={!!value.extractModelResponseRegex}
          >
            <div className="space-y-2">
              <StringInputBuilder
                name="Extract Model Response Regex"
                existingValue={value.extractModelResponseRegex}
                updateProperty={(newVal: string) =>
                  onSubmit({ ...value, extractModelResponseRegex: newVal })
                }
              />
              <Typography.Paragraph2 color="text-neutral-500">
                If present, the model response shown in the chat turn overview (when closed) will be
                extracted using the provided regex.
              </Typography.Paragraph2>
            </div>
          </ExperimentalFeature>
        </div>
      </WrappingComponent>
    </div>
  );
};

export default ChatHistory;
